import React, { useState } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { CaseEpisode, CaseRecord } from '../types';
import { parseTs } from '../logic';

const BORDER = '#E2E8F0';
const MUTED = '#64748B';
const INK = '#0F172A';

const timestampFormat = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

function formatTimestamp(value: string): string {
  const date = parseTs(value);
  return Number.isNaN(date.getTime()) ? value : timestampFormat.format(date);
}

function titleCase(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, character => character.toUpperCase());
}

// Mirror of WATCHDOG_RESOLVED_BY in sbly-dash-collector feed.ts; the only field a human cannot write.
function isWatchdogClosure(episode: CaseEpisode): boolean {
  return episode.resolution.resolvedBy?.toLowerCase() === 'watchdog';
}

function closureLabel(episode: CaseEpisode): string {
  const resolver = episode.resolution.resolvedBy;
  if (isWatchdogClosure(episode)) return 'Retired automatically by watchdog';
  if (episode.status === 'done') return resolver ? `Completed by ${resolver}` : 'Completed';
  if (episode.status === 'dismissed') return resolver ? `Dismissed by ${resolver}` : 'Dismissed';
  if (resolver) return `Closed by ${resolver}`;
  if (episode.status === 'claimed') return 'Still open, claimed';
  if (episode.status === 'open') return 'Still open';
  return titleCase(episode.status);
}

function closureDetail(episode: CaseEpisode): React.ReactNode {
  const resolution = episode.resolution;
  if (isWatchdogClosure(episode)) {
    const reason = resolution.dismissReason?.replace(/^auto:\s*/i, '');
    return reason ? <><Typography component="span" sx={{ fontWeight: 600 }}>Watchdog reason:</Typography> {reason}</> : null;
  }
  if (episode.status === 'dismissed' && resolution.dismissReason) {
    return <><Typography component="span" sx={{ fontWeight: 600 }}>Reason:</Typography> {resolution.dismissReason}</>;
  }
  return null;
}

function Episode({ episode, index, currentItemId }: { episode: CaseEpisode; index: number; currentItemId: string }) {
  const closedAt = episode.resolution.resolvedAt;
  const detail = closureDetail(episode);
  const note = episode.resolution.note;
  const hasHistory = episode.history.length > 0;
  const isCurrent = episode.itemId === currentItemId;

  return (
    <Box sx={{ borderTop: `1px solid ${BORDER}`, pt: 1.5, pb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, color: INK, fontVariantNumeric: 'tabular-nums' }}>Episode {index + 1}</Typography>
        {isCurrent && <Chip label="Current" size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />}
        <Typography variant="caption" sx={{ ml: 'auto', color: MUTED, textTransform: 'capitalize' }}>{titleCase(episode.status)}</Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2, mt: 1, maxWidth: 560 }}>
        <Box>
          <Typography variant="caption" sx={{ display: 'block', color: MUTED }}>Opened</Typography>
          <Typography variant="body2">{formatTimestamp(episode.firstSeen)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ display: 'block', color: MUTED }}>{closedAt ? 'Closed' : 'Last seen'}</Typography>
          <Typography variant="body2">{formatTimestamp(closedAt ?? episode.lastSeen)}</Typography>
        </Box>
      </Box>
      <Typography variant="body2" sx={{ mt: 1, color: INK }}>{closureLabel(episode)}</Typography>
      {detail && <Typography variant="body2" sx={{ mt: 0.25, color: MUTED }}>{detail}</Typography>}
      {episode.resolution.kind && !isWatchdogClosure(episode) && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: MUTED }}>Change type: {titleCase(episode.resolution.kind)}</Typography>
      )}
      {note && !hasHistory && (
        <Typography variant="body2" sx={{ mt: 0.75, maxWidth: '72ch' }}>
          <Box component="span" sx={{ color: MUTED, fontWeight: 600 }}>Resolution note:</Box> {note}
        </Typography>
      )}
      {hasHistory ? (
        <Box sx={{ mt: 1.25, pl: 1.5, borderLeft: `1px solid ${BORDER}` }}>
          <Typography variant="caption" sx={{ color: MUTED, fontWeight: 600 }}>Human history</Typography>
          {episode.history.map((entry, historyIndex) => (
            <Box key={`${entry.changedAt}-${historyIndex}`} sx={{ mt: 0.5 }}>
              <Typography variant="caption" sx={{ display: 'block', color: MUTED }}>
                {formatTimestamp(entry.changedAt)}{entry.actor ? ` · ${entry.actor}` : ''}
              </Typography>
              <Typography variant="body2">{entry.description}</Typography>
              {entry.rationale && entry.rationale !== entry.description && <Typography variant="caption" sx={{ display: 'block', color: MUTED }}>Why: {entry.rationale}</Typography>}
            </Box>
          ))}
        </Box>
      ) : isWatchdogClosure(episode) ? (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: MUTED }}>No human action recorded.</Typography>
      ) : null}
    </Box>
  );
}

export default function CaseHistory({ record, currentItemId }: { record: CaseRecord | null | undefined; currentItemId: string }) {
  // Closed by default: past episodes are reference, not the decision on the page (L, 2026-09-29).
  const [open, setOpen] = useState(false);
  if (record === null) {
    return <Typography variant="caption" sx={{ display: 'block', mt: 4, color: MUTED }}>Case history is unavailable right now.</Typography>;
  }
  if (!record || !record.naturalKey || record.episodeCount < 1 || record.episodes.length === 0) return null;

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: open ? 1.5 : 0, borderBottom: `1px solid ${BORDER}`, pb: 0.75 }}>
        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: MUTED }}>Case history</Typography>
        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>{record.episodeCount}</Typography>
        <Button size="small" aria-expanded={open} aria-controls="case-history-episodes" onClick={() => setOpen(value => !value)} sx={{ minWidth: 0, px: 0.5 }}>
          {open ? 'Hide' : 'Show history'}
        </Button>
      </Box>
      {open && (
        <Box id="case-history-episodes">
          {record.episodes.map((episode, index) => (
            <Episode key={episode.itemId} episode={episode} index={index} currentItemId={currentItemId} />
          ))}
        </Box>
      )}
    </Box>
  );
}
